import { JobDTO } from '@/types';
import { formatDateTimeRangeInCompanyTimeZone } from './dateUtils';
import { formatPhoneNumber } from './phoneUtils';
import { PAYMENT_METHOD_LABELS } from '@/components/PaymentCard';

export function generateJobSmsBody(job: JobDTO, companyTimezone?: string): string {
  const dateStr = job.is_asap
    ? 'ASAP'
    : formatDateTimeRangeInCompanyTimeZone(job.date_executed_start, job.date_executed_end, companyTimezone);
  const contactName = job.contacts?.[0]?.contact?.name;
  // Skip embedding the phone when it's masked to last-4 — the "••• ••• 1234" text is
  // meaningless to the SMS recipient and would look like a garbled number.
  const contactPhoneRaw = job.contacts?.[0]?.contact?.phone;
  const contactPhoneMasked = job.contacts?.[0]?.contact?.phone_masked;
  const contactPhone = contactPhoneMasked ? undefined : contactPhoneRaw;
  const contactParts = [contactName, contactPhone].filter(Boolean);
  const contactStr = contactParts.length > 0 ? contactParts.join(' ') : '';

  const addressStr = [job.address, job.unit_name].filter(Boolean).join(' ');

  const parts = [
    `Job #${job.invoice_number || 'New'} ${dateStr !== 'N/A' ? dateStr : ''}`.trim(),
    addressStr,
    job.description?.trim(),
    contactStr,
    job.job_type?.name,
    job.notes?.trim()
  ].filter(Boolean);

  return parts.join('\n');
}

/**
 * Human-readable job summary for the "Share Job" action.
 * Completed and non-completed jobs use different layouts (see below).
 * Empty lines are dropped, except the schedule line which falls back to "Not scheduled".
 */
export function generateJobShareText(job: JobDTO, isCompleted: boolean, companyTimezone?: string): string {
  const header = `Job #${job.invoice_number || 'New'}`;
  const addressStr = [job.address, job.unit_name].filter(Boolean).join(', ');

  const contactName = job.contacts?.[0]?.contact?.name;
  // Skip a masked (last-4) phone — "••• ••• 1234" is meaningless to a share recipient.
  const contactPhoneRaw = job.contacts?.[0]?.contact?.phone;
  const contactPhone = job.contacts?.[0]?.contact?.phone_masked ? undefined : contactPhoneRaw;
  const contactStr = [contactName, contactPhone ? formatPhoneNumber(contactPhone) : undefined].filter(Boolean).join(' ');

  if (isCompleted) {
    const methods = [...new Set((job.payments || []).map(p => PAYMENT_METHOD_LABELS[p.type]).filter(Boolean))].join(', ');
    const totalLine = `Total $${(job.total || 0).toFixed(2)}${methods ? `  ${methods}` : ''}`;
    const totalParts = (job.expenses || []).reduce((sum, e) => sum + e.amount, 0);
    const partsLine = totalParts > 0 ? `$${totalParts.toFixed(2)} Parts` : undefined;

    return [header, addressStr, contactStr, job.job_type?.name, totalLine, partsLine].filter(Boolean).join('\n');
  }

  const dateStr = formatDateTimeRangeInCompanyTimeZone(job.date_executed_start, job.date_executed_end, companyTimezone);
  const scheduleLine = job.is_asap ? 'ASAP' : dateStr && dateStr !== 'N/A' ? dateStr : 'Not scheduled';

  return [
    header,
    scheduleLine,
    addressStr,
    contactStr,
    job.job_type?.name,
    job.description?.trim(),
    job.notes?.trim(),
  ].filter(Boolean).join('\n');
}
